import { useCallback } from 'react';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Field, FieldProps, Form, Formik } from 'formik';
import { Button } from '@tanium/react-button';
import { FormCheckboxInput } from '@tanium/react-form-checkbox-input';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { BastionTargetAccess } from 'gql/BastionTargetAccess';
import Helper from 'utils/Helper';
import BastionType from 'gql/BastionType';
import OperatingSystem from 'gql/OperatingSystem';
import IDropdownOption from 'common/IDropdownOption';
import FormTextField from '../form/formTextField';
import FormDropDown from '../form/formDropDown';

const UNSPECIFIED_OPERATING_SYSTEM = '';

type BastionTargetAccessForm = {
  adGroups: string;
  description: string;
  name: string;
  launchTemplateName: string;
  autoscalingGroupName: string;
  proxyKMSKeyARN: string;
  bastionType: BastionType;
  operatingSystem: OperatingSystem | typeof UNSPECIFIED_OPERATING_SYSTEM;
  canPortForward: boolean;
};

const defaultCreateForm: BastionTargetAccessForm = {
  adGroups: '',
  description: '',
  name: '',
  launchTemplateName: '',
  autoscalingGroupName: '',
  bastionType: BastionType.PRIVATE,
  operatingSystem: UNSPECIFIED_OPERATING_SYSTEM,
  proxyKMSKeyARN: '',
  canPortForward: false,
};

function mapBastionTargetAccessToForm({
  adGroups,
  description,
  name,
  launchTemplateName,
  autoscalingGroupName,
  proxyKMSKeyARN,
  bastionType,
  operatingSystem,
  canPortForward,
}: BastionTargetAccess): BastionTargetAccessForm {
  return {
    adGroups: adGroups.join(','),
    name,
    launchTemplateName,
    autoscalingGroupName,
    description: description || '',
    proxyKMSKeyARN,
    bastionType,
    operatingSystem: operatingSystem || UNSPECIFIED_OPERATING_SYSTEM,
    canPortForward,
  };
}

export type Operation =
  | {
      type: 'create';
      adGroups: string[];
      description?: string;
      targetOrTargetGroupId: string;
      name: string;
      launchTemplateName: string;
      autoscalingGroupName: string;
      proxyKMSKeyARN: string;
      bastionType: BastionType;
      operatingSystem: OperatingSystem;
      canPortForward: boolean;
    }
  | {
      type: 'update';
      id: string;
      targetOrTargetGroupId: string;
      adGroups: string[];
      description?: string;
      name: string;
      launchTemplateName: string;
      autoscalingGroupName: string;
      proxyKMSKeyARN: string;
      bastionType: BastionType;
      operatingSystem: OperatingSystem;
      canPortForward: boolean;
    }
  | {
      type: 'cancel';
    };

type BastionTargetAccessCRUDModalProps = {
  isOpen: boolean;
  onProcess: (operation: Operation) => void;
  bastionToEdit?: BastionTargetAccess;
  targetOrTargetGroupId: string;
};

const BastionTargetAccessCRUDModal: React.VFC<BastionTargetAccessCRUDModalProps> = ({
  onProcess,
  bastionToEdit,
  targetOrTargetGroupId,
  isOpen,
}) => {
  const { t } = useTranslation();

  const bastionTypes = [
    BastionType.PRIVATE,
    BastionType.PUBLIC,
    BastionType.CPU_OPTIMIZED,
    BastionType.LIMITED_NETWORK,
  ];
  const bastionTypeOptions: IDropdownOption[] = bastionTypes.map((bastionType) => {
    return {
      label: t(`BASTION_TYPE_${bastionType}`),
      value: bastionType,
    };
  });

  const operatingSystemOptions: IDropdownOption[] = [
    { label: t('OPERATING_SYSTEM_LINUX'), value: OperatingSystem.LINUX },
    { label: t('OPERATING_SYSTEM_WINDOWS'), value: OperatingSystem.WINDOWS },
  ];

  const handleSubmit = useCallback(
    (data: BastionTargetAccessForm) => {
      const operatingSystem = data.operatingSystem as OperatingSystem;
      const operation: Operation =
        bastionToEdit === undefined
          ? {
              type: 'create',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              description: data.description,
              targetOrTargetGroupId,
              name: data.name,
              launchTemplateName: data.launchTemplateName,
              autoscalingGroupName: data.autoscalingGroupName,
              proxyKMSKeyARN: data.proxyKMSKeyARN,
              bastionType: data.bastionType,
              operatingSystem,
              canPortForward: data.canPortForward,
            }
          : {
              type: 'update',
              id: bastionToEdit.id,
              targetOrTargetGroupId: bastionToEdit.targetOrTargetGroupID,
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              description: data.description,
              name: data.name,
              launchTemplateName: data.launchTemplateName,
              autoscalingGroupName: data.autoscalingGroupName,
              proxyKMSKeyARN: data.proxyKMSKeyARN,
              bastionType: data.bastionType,
              operatingSystem,
              canPortForward: data.canPortForward,
            };
      onProcess(operation);
    },
    [onProcess, bastionToEdit, targetOrTargetGroupId]
  );

  const handleCancel = useCallback(() => {
    onProcess({
      type: 'cancel',
    });
  }, [onProcess]);

  const validationSchema = yup.object().shape({
    name: yup.string().label(t('BASTION_TARGET_ACCESS_NAME_LABEL')).required(),
    description: yup.string().label(t('DESCRIPTION')).nullable(),
    adGroups: yup.string().label(t('AD_GROUPS')).required(),
    launchTemplateName: yup.string().label(t('BASTION_TARGET_ACCESS_LAUNCH_TEMPLATE_LABEL')).required(),
    proxyKMSKeyARN: yup.string().label(t('BASTION_KMS_KEY_ARN')).nullable(),
    bastionType: yup.string().label(t('BASTION_TYPE')).required(),
    operatingSystem: yup.string().label(t('OPERATING_SYSTEM')).required(),
    canPortForward: yup.string().label(t('BASTION_TARGET_ACCESS_PORT_FORWARD_LABEL')).required(),
  });

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onRequestClose={handleCancel}
      header={
        bastionToEdit === undefined
          ? t('BASTION_TARGET_ACCESS_CREATE_MODAL_HEADER')
          : t('BASTION_TARGET_ACCESS_UPDATE_MODAL_HEADER')
      }
    >
      <Formik
        initialValues={bastionToEdit !== undefined ? mapBastionTargetAccessToForm(bastionToEdit) : defaultCreateForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Stack direction="column" itemSpacing={1}>
            <FormTextField
              fieldName="name"
              label={t('BASTION_TARGET_ACCESS_NAME_LABEL')}
              description={t('BASTION_TARGET_ACCESS_NAME_DESCRIPTION')}
              placeholder={t('BASTION_TARGET_ACCESS_NAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              fieldName="description"
              label={t('DESCRIPTION')}
              description={t('BASTION_TARGET_ACCESS_DESCRIPTION_DESCRIPTION')}
              placeholder={t('BASTION_TARGET_ACCESS_DESCRIPTION_PLACEHOLDER')}
            />
            <FormTextField
              fieldName="adGroups"
              label={t('AD_GROUPS')}
              description={t('AD_GROUPS_DESCRIPTION')}
              placeholder={t('AD_GROUPS_PLACEHOLDER')}
              required
            />
            <FormTextField
              fieldName="launchTemplateName"
              label={t('BASTION_TARGET_ACCESS_LAUNCH_TEMPLATE_LABEL')}
              description={t('BASTION_TARGET_ACCESS_LAUNCH_TEMPLATE_DESCRIPTION')}
              placeholder={t('BASTION_TARGET_ACCESS_LAUNCH_TEMPLATE_PLACEHOLDER')}
              required
            />
            <FormTextField
              fieldName="autoscalingGroupName"
              label={t('BASTION_TARGET_ACCESS_AUTOSCALING_GROUP_LABEL')}
              description={t('BASTION_TARGET_ACCESS_AUTOSCALING_GROUP_DESCRIPTION')}
              placeholder={t('BASTION_TARGET_ACCESS_AUTOSCALING_GROUP_PLACEHOLDER')}
            />
            <FormTextField
              fieldName="proxyKMSKeyARN"
              label={t('BASTION_KMS_KEY_ARN')}
              description={t('BASTION_KMS_KEY_ARN_DESCRIPTION')}
              placeholder={t('BASTION_KMS_KEY_ARN_PLACEHOLDER')}
            />
            <FormDropDown
              fieldName="bastionType"
              label={t('BASTION_TYPE')}
              description={t('BASTION_TARGET_ACCESS_TYPE_DESCRIPTION')}
              options={bastionTypeOptions}
              required
            />
            <FormDropDown
              fieldName="operatingSystem"
              label={t('OPERATING_SYSTEM')}
              description={t('OPERATING_SYSTEM_DESCRIPTION')}
              options={operatingSystemOptions}
              required
            />
            <Field name="canPortForward">
              {({ field }: FieldProps): React.ReactElement => (
                <FormCheckboxInput
                  label={t('BASTION_TARGET_ACCESS_PORT_FORWARD_LABEL')}
                  data-testid="canPortForward"
                  {...field}
                  name={field.name}
                  checked={field.value}
                />
              )}
            </Field>

            <Stack itemSpacing={1}>
              <Button data-testid="submit" type="submit">
                {bastionToEdit === undefined ? t('CREATE') : t('UPDATE')}
              </Button>
              <Button type="button" data-testid="cancel" variant="secondary" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default BastionTargetAccessCRUDModal;
